/**
 * OpenAI Text-to-Speech Hook
 *
 * React hook for OpenAI TTS voice synthesis and playback.
 * Simpler than Resemble - no persistent storage, ephemeral audio only.
 *
 * Features:
 * - In-memory caching (per component instance)
 * - Full playback controls (play/pause/stop/seek)
 * - Automatic cleanup on unmount
 * - HIPAA compliant (no data persistence)
 *
 * Usage:
 * ```tsx
 * const voice = useOpenAITTS({ voiceId: 'nova' });
 * voice.speak('Hello world');
 * ```
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:hook:use-openai-tts');
import type { TTSVoice } from '@ctrl-enter/types';
import { useSynthesizeTTSMutation } from './use-gateway-queries';

// ============================================================================
// VOICE PREFERENCES (localStorage)
// ============================================================================

const VOICE_STORAGE_KEY = 'openai-tts-voice';
const DEFAULT_VOICE: TTSVoice = 'nova';

/**
 * Validate if a string is a valid TTSVoice
 */
const isValidVoice = (voice: string): voice is TTSVoice => {
  return ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'].includes(voice);
};

/**
 * Get the user's preferred TTS voice from localStorage
 * @returns The selected voice or 'nova' as default
 */
export const getPreferredVoice = (): TTSVoice => {
  try {
    const stored = localStorage.getItem(VOICE_STORAGE_KEY);
    if (stored && isValidVoice(stored)) {
      return stored as TTSVoice;
    }
  } catch (error) {
    logger.warn('[voice-preferences] Failed to read from localStorage:', error);
  }
  return DEFAULT_VOICE;
};

/**
 * Set the user's preferred TTS voice in localStorage
 * @param voice - The voice to set as preferred
 */
export const setPreferredVoice = (voice: TTSVoice): void => {
  try {
    localStorage.setItem(VOICE_STORAGE_KEY, voice);
  } catch (error) {
    logger.error('[voice-preferences] Failed to write to localStorage:', error);
  }
};

// ============================================================================
// HOOK TYPES
// ============================================================================

/**
 * Voice playback state
 */
export type VoicePlaybackState = 'idle' | 'loading' | 'playing' | 'paused' | 'stopped' | 'error';

/**
 * Voice player state with progress
 */
export interface VoicePlayerState {
  state: VoicePlaybackState;
  currentTime: number;
  duration: number;
  progress: number; // 0-100
  error: Error | null;
}

/**
 * Hook options
 */
export interface UseOpenAITTSOptions {
  /** Voice ID (alloy, echo, fable, onyx, nova, shimmer) */
  voiceId?: TTSVoice;
  /** Playback speed (0.25 - 4.0) */
  speed?: number;
  /** Optional instructions for tone/style */
  instructions?: string;
  /** Auto-play after synthesis */
  autoPlay?: boolean;
  /** Callback when playback completes */
  onComplete?: () => void;
  /** Callback when playback state changes */
  onStateChange?: (state: VoicePlaybackState) => void;
  /** Callback when error occurs */
  onError?: (error: Error) => void;
}

/**
 * Hook return value
 */
export interface UseOpenAITTSResult {
  // State
  state: VoicePlaybackState;
  isPlaying: boolean;
  isPaused: boolean;
  isStopped: boolean;
  isLoading: boolean;
  isIdle: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  error: Error | null;
  currentText: string | null;

  // Actions
  speak: (text: string) => Promise<void>;
  play: () => void;
  pause: () => void;
  stop: () => void;
  reset: () => void;
  toggle: () => void;
  resume: () => void;
  seekForward: (seconds: number) => void;
  seekBackward: (seconds: number) => void;
}

/**
 * OpenAI TTS Hook
 */
export const useOpenAITTS = (options: UseOpenAITTSOptions = {}): UseOpenAITTSResult => {
  const {
    voiceId = 'nova',
    speed = 1.0,
    instructions,
    autoPlay = false,
    onComplete,
    onStateChange,
    onError,
  } = options;

  // State management
  const [state, setState] = useState<VoicePlaybackState>('idle');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [currentText, setCurrentText] = useState<string | null>(null);

  // Audio element ref
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // In-memory cache for current text
  const cachedAudioRef = useRef<{ text: string; audioUrl: string } | null>(null);

  // AbortController to remove all event listeners at once
  const abortControllerRef = useRef<AbortController | null>(null);

  // API mutation
  const synthesizeMutation = useSynthesizeTTSMutation();

  /**
   * Update playback state and notify listeners
   */
  const updateState = useCallback(
    (newState: VoicePlaybackState) => {
      setState(newState);
      onStateChange?.(newState);
    },
    [onStateChange]
  );

  /**
   * Handle errors with proper cleanup
   */
  const handleError = useCallback(
    (err: Error) => {
      logger.error('[useOpenAITTS] Error:', err);
      setError(err);
      updateState('error');
      onError?.(err);
    },
    [onError, updateState]
  );

  /**
   * Start tracking playback progress
   */
  const startProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    progressIntervalRef.current = setInterval(() => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime);
        setDuration(audioRef.current.duration || 0);
      }
    }, 100); // Update every 100ms for smooth progress
  }, []);

  /**
   * Stop tracking playback progress
   */
  const stopProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  /**
   * Clean up audio resources completely
   */
  const cleanup = useCallback(() => {
    stopProgressTracking();

    // Abort all event listeners first
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current.load();
      } catch (e) {
        logger.info('[cleanup] Error during audio cleanup:', e);
      }
      audioRef.current = null;
    }

    // Clean up cached blob URL
    if (cachedAudioRef.current) {
      try {
        URL.revokeObjectURL(cachedAudioRef.current.audioUrl);
      } catch (e) {
        logger.info('[cleanup] Error revoking blob URL:', e);
      }
      cachedAudioRef.current = null;
    }

    setCurrentTime(0);
    setDuration(0);
  }, [stopProgressTracking]);

  /**
   * Helper function to create audio from base64 data and play it
   */
  const createAndPlayAudio = useCallback(
    async (audioData: string, textForCache: string, format = 'opus') => {
      try {
        // Convert base64 to blob
        const binaryString = atob(audioData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const mimeType =
          format === 'opus'
            ? 'audio/ogg; codecs=opus'
            : format === 'wav'
              ? 'audio/wav'
              : 'audio/mpeg';
        const audioBlob = new Blob([bytes], { type: mimeType });

        // Revoke old cached URL if it exists
        if (cachedAudioRef.current) {
          URL.revokeObjectURL(cachedAudioRef.current.audioUrl);
          cachedAudioRef.current = null;
        }

        // Create blob URL and cache it
        const blobUrl = URL.createObjectURL(audioBlob);
        cachedAudioRef.current = { text: textForCache, audioUrl: blobUrl };

        // Create audio element
        const audio = new Audio();
        audioRef.current = audio;

        // Create new AbortController for this audio instance
        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;

        // Set up audio event listeners with signal for automatic cleanup
        audio.addEventListener(
          'loadedmetadata',
          () => {
            setDuration(audio.duration);
          },
          { signal }
        );

        audio.addEventListener(
          'timeupdate',
          () => {
            setCurrentTime(audio.currentTime);
          },
          { signal }
        );

        audio.addEventListener(
          'ended',
          () => {
            // Keep audio element alive but mark as stopped for replay
            updateState('stopped');
            stopProgressTracking();
            // Reset to beginning for easy replay
            if (audioRef.current) {
              audioRef.current.currentTime = 0;
              setCurrentTime(0);
            }
            onComplete?.();
          },
          { signal }
        );

        audio.addEventListener(
          'error',
          e => {
            handleError(new Error(`Audio playback error: ${e.type}`));
          },
          { signal }
        );

        // Set audio source from blob
        audio.src = blobUrl;

        updateState('stopped');

        // Auto-play if enabled
        if (autoPlay) {
          await audio.play();
          updateState('playing');
          startProgressTracking();
        }
      } catch (error) {
        // Clean up blob URL on any error during setup
        if (cachedAudioRef.current) {
          URL.revokeObjectURL(cachedAudioRef.current.audioUrl);
          cachedAudioRef.current = null;
        }
        throw error;
      }
    },
    [autoPlay, updateState, startProgressTracking, stopProgressTracking, onComplete, handleError]
  );

  /**
   * Synthesize text to speech
   */
  const speak = useCallback(
    async (text: string): Promise<void> => {
      if (!text || text.trim().length === 0) {
        handleError(new Error('Text cannot be empty'));
        return;
      }

      try {
        // Clean up any existing audio before creating new
        cleanup();
        setError(null);
        setCurrentText(text);
        updateState('loading');

        logger.info('[useOpenAITTS] Synthesizing speech:', {
          textLength: text.length,
          voice: voiceId,
          speed,
        });

        // Call TTS API
        const response = await synthesizeMutation.mutateAsync({
          body: {
            text,
            voice: voiceId,
            speed,
            instructions,
          },
        });

        if (response.status !== 200) {
          throw new Error('Failed to synthesize speech');
        }

        logger.info(
          '[useOpenAITTS] Audio synthesized:',
          JSON.stringify(
            {
              sizeKB: (response.body.sizeBytes / 1024).toFixed(2),
              format: response.body.format,
            },
            null,
            2
          )
        );

        // Track TTS voice generation
        import('../services/analytics').then(({ mixpanel }) => {
          mixpanel.track('Voice Generated from Text', {
            textLength: text.length,
            voice: voiceId,
            speed,
            format: response.body.format,
            sizeKB: (response.body.sizeBytes / 1024).toFixed(2),
          });
        });

        await createAndPlayAudio(response.body.audio, text, response.body.format);
      } catch (err) {
        handleError(err instanceof Error ? err : new Error('Unknown error during synthesis'));
      }
    },
    [
      voiceId,
      speed,
      instructions,
      cleanup,
      handleError,
      updateState,
      synthesizeMutation,
      createAndPlayAudio,
    ]
  );

  /**
   * Play audio
   */
  const play = useCallback(() => {
    if (audioRef.current && state !== 'playing') {
      audioRef.current.play();
      updateState('playing');
      startProgressTracking();
    }
  }, [state, updateState, startProgressTracking]);

  /**
   * Pause audio
   */
  const pause = useCallback(() => {
    if (audioRef.current && state === 'playing') {
      audioRef.current.pause();
      updateState('paused');
      stopProgressTracking();
    }
  }, [state, updateState, stopProgressTracking]);

  /**
   * Stop audio (pause and reset to beginning)
   */
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
    updateState('stopped');
    stopProgressTracking();
  }, [updateState, stopProgressTracking]);

  /**
   * Reset and destroy audio completely
   */
  const reset = useCallback(() => {
    logger.info('[useOpenAITTS] Resetting voice');
    cleanup();
    setError(null);
    setCurrentText(null);
    updateState('idle');
  }, [cleanup, updateState]);

  /**
   * Toggle play/pause
   */
  const toggle = useCallback(() => {
    if (state === 'playing') {
      pause();
    } else if (state === 'paused' || state === 'stopped') {
      play();
    }
  }, [state, play, pause]);

  /**
   * Resume playback (alias for play)
   */
  const resume = useCallback(() => {
    play();
  }, [play]);

  /**
   * Seek forward by specified seconds
   */
  const seekForward = useCallback((seconds: number) => {
    if (audioRef.current) {
      const newTime = Math.min(audioRef.current.currentTime + seconds, audioRef.current.duration);
      audioRef.current.currentTime = newTime;
    }
  }, []);

  /**
   * Seek backward by specified seconds
   */
  const seekBackward = useCallback((seconds: number) => {
    if (audioRef.current) {
      const newTime = Math.max(audioRef.current.currentTime - seconds, 0);
      audioRef.current.currentTime = newTime;
    }
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Computed state
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return {
    // State
    state,
    isPlaying: state === 'playing',
    isPaused: state === 'paused',
    isStopped: state === 'stopped',
    isLoading: state === 'loading',
    isIdle: state === 'idle',
    currentTime,
    duration,
    progress,
    error,
    currentText,

    // Actions
    speak,
    play,
    pause,
    stop,
    reset,
    toggle,
    resume,
    seekForward,
    seekBackward,
  };
};
